import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Chip, Notice, Tag, useToast } from '@kollez/design-system';
import {
  useCreateWorkflow,
  useSkillTaxonomy,
  useExams,
  type CreateWorkflowInput,
  type WorkflowStageLite,
} from '@/lib/queries';
import { SkillPicker } from '@/components/SkillPicker';

/**
 * /workflows/new — author a Workflow template (v1.6 §7.6).
 *
 * Structured stage editor: each stage has its own card with the required fields inline
 * (key/label/order/author/reversibility/allowedNext) and collapsible advanced sections for
 * gates + monetization + satisfiedByBadge + emitsBadge.
 *
 * Advanced escape hatch: an "Edit as JSON" toggle at the bottom flips the whole stage list
 * into a textarea for power users who want to paste a full template. The two views stay in
 * sync — flipping back re-parses the JSON and rebuilds the cards.
 */

const AUTHORS = ['student', 'partner', 'tenant', 'kollez_ops', 'automated', 'trainer'] as const;
const REVERSIBILITY = ['provisional', 'reversible_with_cost', 'irreversible'] as const;
const STAGE_TYPES = [
  '',
  'application',
  'shortlist',
  'assessment',
  'interview',
  'offer',
  'onboarding',
  'training',
  'custom',
] as const;
const MONETIZATION_MODELS = [
  'student_paid',
  'partner_paid_per_placement',
  'partner_paid_per_posting',
  'sponsored',
  'free',
] as const;
const CHARGE_ON = ['enter', 'exit', 'gate_passed'] as const;
const BADGE_TAXONOMIES = ['skill', 'psychocognitive', 'realwork', 'language', 'soft_skill', 'domain'] as const;
const ISSUER_TIERS = ['tier_1', 'tier_2', 'any'] as const;

const STARTER_STAGES: WorkflowStageLite[] = [
  { key: 'applied', label: 'Applied', order: 1, author: 'student', reversibilityClass: 'provisional', allowedNext: ['review'] },
  { key: 'review', label: 'Under review', order: 2, author: 'tenant', reversibilityClass: 'reversible_with_cost', allowedNext: ['offered', 'rejected'] },
  { key: 'offered', label: 'Offered', order: 3, author: 'partner', reversibilityClass: 'irreversible', allowedNext: [] },
  { key: 'rejected', label: 'Not this time', order: 4, author: 'tenant', reversibilityClass: 'irreversible', allowedNext: [] },
];

function blankStage(order: number): WorkflowStageLite {
  return {
    key: `stage_${order}`,
    label: 'New stage',
    order,
    author: 'tenant',
    reversibilityClass: 'reversible_with_cost',
    allowedNext: [],
  };
}

export function WorkflowNewPage() {
  const nav = useNavigate();
  const toast = useToast();
  const create = useCreateWorkflow();

  const [name, setName] = useState('New workflow');
  const [scope, setScope] = useState<'GLOBAL' | 'TENANT' | 'PARTNER'>('TENANT');
  const [type, setType] = useState('Placement_OnCampus');
  const [description, setDescription] = useState('');
  const [stages, setStages] = useState<WorkflowStageLite[]>(STARTER_STAGES);

  const [jsonMode, setJsonMode] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Track which stage cards have advanced sections open. Keyed by stage key.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const stageKeys = useMemo(() => new Set(stages.map((s) => s.key)), [stages]);

  // Detect graph errors before submit so the user gets inline hints, not just server 400s.
  const graphErrors = useMemo(() => {
    const errs: string[] = [];
    if (stages.length === 0) errs.push('Template has no stages.');
    const seen = new Set<string>();
    for (const s of stages) {
      if (!s.key.trim()) errs.push(`Stage at order ${s.order} has an empty key.`);
      if (seen.has(s.key)) errs.push(`Duplicate stage key '${s.key}'.`);
      seen.add(s.key);
    }
    for (const s of stages) {
      for (const next of s.allowedNext) {
        if (!seen.has(next)) errs.push(`Stage '${s.key}' points at unknown next '${next}'.`);
      }
      if (s.stageType === 'assessment' && !s.assessmentId) {
        errs.push(`Stage '${s.key}' is type=assessment but no assessment is picked.`);
      }
    }
    return errs;
  }, [stages]);

  function patchStage(idx: number, patch: Partial<WorkflowStageLite>) {
    setStages((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  }
  function addStage() {
    setStages((prev) => [...prev, blankStage(prev.length + 1)]);
  }
  function removeStage(idx: number) {
    setStages((prev) => prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i + 1 })));
  }
  function moveStage(idx: number, dir: -1 | 1) {
    setStages((prev) => {
      const target = idx + dir;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(idx, 1);
      next.splice(target, 0, item!);
      return next.map((s, i) => ({ ...s, order: i + 1 }));
    });
  }

  function enterJsonMode() {
    setJsonText(JSON.stringify(stages, null, 2));
    setJsonError(null);
    setJsonMode(true);
  }
  function exitJsonMode() {
    try {
      const parsed = JSON.parse(jsonText);
      if (!Array.isArray(parsed)) {
        setJsonError('Top-level value must be an array of stages.');
        return;
      }
      setStages(parsed as WorkflowStageLite[]);
      setJsonError(null);
      setJsonMode(false);
    } catch (err) {
      setJsonError(err instanceof Error ? err.message : 'invalid JSON');
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (graphErrors.length > 0) {
      toast.show({ tone: 'warning', message: 'Fix the graph errors before saving.' });
      return;
    }
    const body: CreateWorkflowInput = {
      name: name.trim(),
      scope,
      type,
      description: description.trim() || undefined,
      stages,
    };
    try {
      const res = await create.mutateAsync(body);
      toast.show({ message: `Draft saved — ${res.workflow.name}` });
      nav(`/workflows/${res.workflow._id}`);
    } catch (err) {
      toast.show({
        tone: 'warning',
        message: err instanceof Error ? err.message : 'Save failed.',
      });
    }
  }

  return (
    <form className="stack" onSubmit={submit}>
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">v1.6 §7.6 · Author a template</p>
          <h1 className="h-page">New workflow</h1>
          <p className="subhead">Draft it, review the graph, then publish. Stays a draft until you hit Publish on the detail page.</p>
        </div>
        <div className="ph-actions">
          <Button type="button" variant="ghost" onClick={() => nav('/workflows')}>Cancel</Button>
          <Button type="submit" variant="primary" loading={create.isPending} disabled={graphErrors.length > 0}>
            Save draft
          </Button>
        </div>
      </div>

      <Card padding="lg">
        <div className="grid gap-3 md:grid-cols-2">
          <label className="grid gap-1 text-sm">
            <span>Name</span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} required minLength={3} maxLength={200} />
          </label>
          <label className="grid gap-1 text-sm">
            <span>Type</span>
            <input className="input" value={type} onChange={(e) => setType(e.target.value)} required />
          </label>
          <label className="grid gap-1 text-sm">
            <span>Scope</span>
            <select className="input" value={scope} onChange={(e) => setScope(e.target.value as 'GLOBAL' | 'TENANT' | 'PARTNER')}>
              <option value="GLOBAL">GLOBAL (Kollez-owned)</option>
              <option value="TENANT">TENANT (your tenant only)</option>
              <option value="PARTNER">PARTNER</option>
            </select>
          </label>
          <label className="grid gap-1 text-sm md:col-span-2">
            <span>Description</span>
            <textarea className="input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000} />
          </label>
        </div>
      </Card>

      <Card padding="lg">
        <div className="flex items-center justify-between mb-3">
          <h2 className="h-section">Stages</h2>
          <div className="flex items-center gap-2">
            {jsonMode ? (
              <>
                <Button type="button" variant="ghost" onClick={() => { setJsonMode(false); setJsonError(null); }}>Discard JSON edits</Button>
                <Button type="button" variant="primary" onClick={exitJsonMode}>Apply JSON</Button>
              </>
            ) : (
              <>
                <Button type="button" variant="ghost" onClick={enterJsonMode}>Edit as JSON</Button>
                <Button type="button" variant="primary" onClick={addStage}>+ Add stage</Button>
              </>
            )}
          </div>
        </div>

        {graphErrors.length > 0 ? (
          <Notice tone="warning" className="mb-3">
            <b>Graph errors ({graphErrors.length}):</b>
            <ul className="list-disc list-inside">
              {graphErrors.map((e) => <li key={e}>{e}</li>)}
            </ul>
          </Notice>
        ) : (
          <p className="cell-sub mb-3">{stages.length} stages · graph looks clean.</p>
        )}

        {jsonMode ? (
          <>
            <textarea
              className="input font-mono text-xs"
              style={{ minHeight: 320 }}
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
            />
            {jsonError ? <Notice tone="warning" className="mt-2"><b>Parse error:</b> {jsonError}</Notice> : null}
          </>
        ) : (
          <div className="grid gap-3">
            {stages.map((stage, idx) => (
              <StageCard
                key={stage.key + ':' + idx}
                stage={stage}
                idx={idx}
                total={stages.length}
                allStageKeys={Array.from(stageKeys).filter((k) => k !== stage.key)}
                expanded={expanded[stage.key] ?? false}
                onToggleExpanded={() => setExpanded((e) => ({ ...e, [stage.key]: !e[stage.key] }))}
                onPatch={(p) => patchStage(idx, p)}
                onRemove={() => removeStage(idx)}
                onMoveUp={() => moveStage(idx, -1)}
                onMoveDown={() => moveStage(idx, 1)}
              />
            ))}
          </div>
        )}
      </Card>
    </form>
  );
}

interface StageCardProps {
  stage: WorkflowStageLite;
  idx: number;
  total: number;
  allStageKeys: string[];
  expanded: boolean;
  onToggleExpanded: () => void;
  onPatch: (p: Partial<WorkflowStageLite>) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

function StageCard({ stage, idx, total, allStageKeys, expanded, onToggleExpanded, onPatch, onRemove, onMoveUp, onMoveDown }: StageCardProps) {
  const toggleAllowedNext = (k: string) => {
    const has = stage.allowedNext.includes(k);
    onPatch({ allowedNext: has ? stage.allowedNext.filter((x) => x !== k) : [...stage.allowedNext, k] });
  };
  // Only fetch when the user has picked stageType='assessment' on any card — cheap once cached.
  const exams = useExams();
  const liveAssessments = useMemo(
    () => (exams.data?.assessments ?? []).filter((a) => a.status === 'live' || a.status === 'published'),
    [exams.data?.assessments],
  );

  return (
    <div className="cell" style={{ padding: '12px 14px' }}>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Tag pressed>#{stage.order}</Tag>
        <b className="text-sm">{stage.label || <em className="cell-sub">unnamed</em>}</b>
        <Chip tone="default">{stage.author}</Chip>
        <Chip tone={stage.reversibilityClass === 'irreversible' ? 'danger' : stage.reversibilityClass === 'reversible_with_cost' ? 'warning' : 'success'}>
          {stage.reversibilityClass}
        </Chip>
        <div className="flex items-center gap-1 ml-auto">
          <Button type="button" variant="ghost" size="sm" onClick={onMoveUp} disabled={idx === 0}>↑</Button>
          <Button type="button" variant="ghost" size="sm" onClick={onMoveDown} disabled={idx === total - 1}>↓</Button>
          <Button type="button" variant="ghost" size="sm" onClick={onToggleExpanded}>{expanded ? 'Hide advanced' : 'Advanced'}</Button>
          <Button type="button" variant="ghost" size="sm" onClick={onRemove}>Remove</Button>
        </div>
      </div>

      <div className="grid gap-2 md:grid-cols-2">
        <label className="grid gap-1 text-sm">
          <span>Key <span className="cell-sub">(unique, kebab_or_snake)</span></span>
          <input className="input" value={stage.key} onChange={(e) => onPatch({ key: e.target.value.trim() })} required />
        </label>
        <label className="grid gap-1 text-sm">
          <span>Label</span>
          <input className="input" value={stage.label} onChange={(e) => onPatch({ label: e.target.value })} required maxLength={120} />
        </label>
        <label className="grid gap-1 text-sm">
          <span>Author hat</span>
          <select className="input" value={stage.author} onChange={(e) => onPatch({ author: e.target.value })}>
            {AUTHORS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
        <label className="grid gap-1 text-sm">
          <span>Reversibility</span>
          <select className="input" value={stage.reversibilityClass} onChange={(e) => onPatch({ reversibilityClass: e.target.value })}>
            {REVERSIBILITY.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
        <label className="grid gap-1 text-sm">
          <span>Stage type <span className="cell-sub">(optional — 'assessment' unlocks the picker)</span></span>
          <select
            className="input"
            value={stage.stageType ?? ''}
            onChange={(e) => {
              const next = e.target.value as WorkflowStageLite['stageType'] | '';
              // Clearing to '' or switching away from 'assessment' drops the ref so the
              // server-side validator doesn't complain about a stale assessmentId.
              const nextStageType = next === '' ? undefined : next;
              const shouldKeepRef = nextStageType === 'assessment';
              onPatch({
                stageType: nextStageType,
                assessmentId: shouldKeepRef ? stage.assessmentId : undefined,
              });
            }}
          >
            {STAGE_TYPES.map((t) => <option key={t || '(none)'} value={t}>{t || '(none)'}</option>)}
          </select>
        </label>
        {stage.stageType === 'assessment' ? (
          <label className="grid gap-1 text-sm md:col-span-2">
            <span>Assessment {stage.assessmentId ? null : <span className="cell-sub">(required)</span>}</span>
            <select
              className="input"
              value={stage.assessmentId ?? ''}
              onChange={(e) => onPatch({ assessmentId: e.target.value || undefined })}
              disabled={exams.isLoading}
            >
              <option value="">Pick an assessment…</option>
              {liveAssessments.map((a) => (
                <option key={a._id} value={a._id}>{a.name ?? a.title ?? a._id}</option>
              ))}
            </select>
            <span className="cell-sub">Only live/published assessments in your tenant appear.</span>
          </label>
        ) : null}
      </div>

      <div className="mt-2">
        <div className="cell-sub mb-1">Allowed next (click to toggle — empty = terminal stage)</div>
        <div className="flex flex-wrap gap-1">
          {allStageKeys.length === 0 ? (
            <span className="cell-sub"><em>no other stages defined yet</em></span>
          ) : allStageKeys.map((k) => (
            <button
              type="button"
              key={k}
              className={`tag ${stage.allowedNext.includes(k) ? 'tag-pressed' : ''}`}
              onClick={() => toggleAllowedNext(k)}
            >
              {k}
            </button>
          ))}
        </div>
      </div>

      {expanded ? (
        <div className="mt-3 grid gap-3" style={{ borderTop: '1px solid rgb(var(--line-tertiary))', paddingTop: 12 }}>
          <MonetizationEditor stage={stage} onPatch={onPatch} />
          <SatisfiedByBadgeEditor stage={stage} onPatch={onPatch} />
          <EmitsBadgeEditor stage={stage} onPatch={onPatch} />
        </div>
      ) : null}
    </div>
  );
}

function MonetizationEditor({ stage, onPatch }: { stage: WorkflowStageLite; onPatch: (p: Partial<WorkflowStageLite>) => void }) {
  const m = stage.monetization;
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <b className="text-sm">Monetization</b>
        {m ? (
          <Button type="button" variant="ghost" size="sm" onClick={() => onPatch({ monetization: undefined })}>Remove</Button>
        ) : (
          <Button type="button" variant="ghost" size="sm" onClick={() => onPatch({ monetization: { model: 'free' } })}>+ Add</Button>
        )}
      </div>
      {m ? (
        <div className="grid gap-2 md:grid-cols-3">
          <label className="grid gap-1 text-sm">
            <span>Model</span>
            <select className="input" value={m.model} onChange={(e) => onPatch({ monetization: { ...m, model: e.target.value } })}>
              {MONETIZATION_MODELS.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </label>
          <label className="grid gap-1 text-sm">
            <span>Charge on</span>
            <select className="input" value={m.chargeOn ?? ''} onChange={(e) => onPatch({ monetization: { ...m, chargeOn: e.target.value || undefined } })}>
              <option value="">(none)</option>
              {CHARGE_ON.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </label>
          <label className="grid gap-1 text-sm">
            <span>Fee (INR)</span>
            <input
              type="number"
              min={0}
              className="input"
              value={m.fee?.amountInr ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '') onPatch({ monetization: { ...m, fee: undefined } });
                else onPatch({ monetization: { ...m, fee: { amountInr: Number(v), currency: 'INR' } } });
              }}
            />
          </label>
        </div>
      ) : null}
    </div>
  );
}

// SkillPicker extracted to @/components/SkillPicker (Step 31) — imported at top of file.

function SatisfiedByBadgeEditor({ stage, onPatch }: { stage: WorkflowStageLite; onPatch: (p: Partial<WorkflowStageLite>) => void }) {
  const b = stage.satisfiedByBadge;
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <b className="text-sm">Auto-satisfy via badge</b>
        {b ? (
          <Button type="button" variant="ghost" size="sm" onClick={() => onPatch({ satisfiedByBadge: undefined })}>Remove</Button>
        ) : (
          <Button type="button" variant="ghost" size="sm" onClick={() => onPatch({ satisfiedByBadge: { skill: '', minLevel: 'basic' } })}>+ Add</Button>
        )}
      </div>
      {b ? (
        <div className="grid gap-2 md:grid-cols-3">
          <label className="grid gap-1 text-sm">
            <span>Skill slug</span>
            <SkillPicker
              value={b.skill}
              onChange={(v) => onPatch({ satisfiedByBadge: { ...b, skill: v } })}
              placeholder="e.g. aptitude"
              listId={`satisfy-skill-${stage.key}`}
            />
          </label>
          <label className="grid gap-1 text-sm">
            <span>Min level</span>
            <input className="input" placeholder="basic | standard | advanced" value={b.minLevel} onChange={(e) => onPatch({ satisfiedByBadge: { ...b, minLevel: e.target.value } })} />
          </label>
          <label className="grid gap-1 text-sm">
            <span>Issuer tier</span>
            <select
              className="input"
              value={(b as { requireIssuerTier?: string }).requireIssuerTier ?? 'any'}
              onChange={(e) => onPatch({ satisfiedByBadge: { ...b, requireIssuerTier: e.target.value as 'tier_1' | 'tier_2' | 'any' } })}
            >
              {ISSUER_TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
        </div>
      ) : null}
    </div>
  );
}

function EmitsBadgeEditor({ stage, onPatch }: { stage: WorkflowStageLite; onPatch: (p: Partial<WorkflowStageLite>) => void }) {
  const e = stage.emitsBadge;
  const taxo = useSkillTaxonomy();
  // If the user picks a known skill, auto-preselect its taxonomy so `emitsBadge.taxonomy`
  // and the taxonomy row stay in sync (server doesn't enforce it, but Badge issuance
  // reads Badge.taxonomy separately, so mismatches surface as confusing dashboards).
  const knownTaxonomy = useMemo(() => {
    if (!e?.skill) return null;
    const raw = e.skill.trim().toLowerCase();
    const row = taxo.data?.rows.find(
      (r) => r.slug.toLowerCase() === raw || r.aliases.some((a) => a.toLowerCase() === raw),
    );
    return row?.taxonomy ?? null;
  }, [e?.skill, taxo.data?.rows]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <b className="text-sm">Emit badge on completion</b>
        {e ? (
          <Button type="button" variant="ghost" size="sm" onClick={() => onPatch({ emitsBadge: undefined })}>Remove</Button>
        ) : (
          <Button type="button" variant="ghost" size="sm" onClick={() => onPatch({ emitsBadge: { skill: '', taxonomy: 'skill' } })}>+ Add</Button>
        )}
      </div>
      {e ? (
        <div className="grid gap-2 md:grid-cols-3">
          <label className="grid gap-1 text-sm">
            <span>Skill slug</span>
            <SkillPicker
              value={e.skill}
              onChange={(v) => {
                // If the picked slug resolves to a known taxonomy row, keep taxonomy aligned.
                const raw = v.trim().toLowerCase();
                const row = taxo.data?.rows.find(
                  (r) => r.slug.toLowerCase() === raw || r.aliases.some((a) => a.toLowerCase() === raw),
                );
                onPatch({ emitsBadge: { ...e, skill: v, ...(row ? { taxonomy: row.taxonomy } : {}) } });
              }}
              placeholder="e.g. real-work-internship"
              listId={`emit-skill-${stage.key}`}
            />
          </label>
          <label className="grid gap-1 text-sm">
            <span>Taxonomy {knownTaxonomy && knownTaxonomy !== e.taxonomy ? <span className="cell-sub">(mismatched)</span> : null}</span>
            <select className="input" value={e.taxonomy} onChange={(ev) => onPatch({ emitsBadge: { ...e, taxonomy: ev.target.value } })}>
              {BADGE_TAXONOMIES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
        </div>
      ) : null}
    </div>
  );
}
